import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import * as BenchlingUtil from 'BenchlingUtil';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {ProcessClientOrigin, SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import {ColumnAccessorKey} from 'nav/expanded/ListingRendererConstants';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import SettingsIconDropdown from 'nav/sidepanel/SettingsIconDropdown';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import {
  getAvailableCustomSortables,
  shouldShowSortDropdown,
} from 'processes/dashboard/processes-search-platform/listing-renderers/getAvailableSorts';
import {
  defaultProcessManagementSearchParams,
  getDefaultFiltersForClientOrigin,
} from 'processes/dashboard/ProcessSearchParams';
import {getEnabledClientOrigins} from 'processes/ProcessConstants';
import * as FilterUtil from 'search/FilterUtil';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {
  Filter,
  FilterKey,
  ListableItem,
  SearchParams,
  SearchType,
  SortKey,
  QuickFilterKey,
} from 'search/SearchConstants';
import * as SearchPreferences from 'search/SearchPreferences';
import * as SearchUtil from 'search/SearchUtil';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';

import areSelectedTypesExactly, {areSelectedTypesSubset} from './areSelectedTypesExactly';
import NewProcessDropdown from './NewProcessDropdown';
import {
  getAvailablePrimaryFilters,
  getAvailableSecondaryFilters,
} from './processes-search-platform/getAvailableFilters';
import {
  getAvailableTypes,
  getDefaultTypes,
  getIsTypeRequired,
} from './processes-search-platform/getAvailableTypes';
import {
  areProcessDashboardModelTypesSupported,
  SidepanelProcessItem,
} from './ProcessManagementSidepanelConstants';
import * as ProcessManagementSidepanelPreferences from './ProcessManagementSidepanelPreferences';
import ProcessManagementToolbar from './ProcessManagementToolbar';

const PROCESS_SORT_KEYS = [SortKey.SORT_CREATED_AT_KEY, SortKey.SORT_NAME_KEY, SortKey.SORT_IDENTIFIER_KEY];

function getSelectedClientOrigin(searchParams: SearchParams): ProcessClientOrigin {
  const selectedClientOrigin = FilterUtil.getFilterValueByKey(
    searchParams.filters,
    FilterKey.PROCESS_CLIENT_ORIGIN
  ) as ProcessClientOrigin;

  assert(
    Object.values(ProcessClientOrigin).includes(selectedClientOrigin),
    `Expected client origin to be one of ${Object.values(ProcessClientOrigin)}`,
    {selectedClientOrigin}
  );
  return selectedClientOrigin;
}

function getSelectedTypes(searchParams: SearchParams): Array<string> {
  const typeFilterValue = FilterUtil.getFilterValueByKey(
    searchParams.filters,
    FilterKey.TYPES
  ) as Array<string>;
  return typeFilterValue;
}

interface SidepanelProps {
  isExpanded: boolean;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  activeItemId: string | null;
}

export type ProcessManagementSidepanelProps = SidepanelProps & SearchProps;

export function ProcessManagementSidepanelCrumbs(props: {
  activeSavedSearch?: SavedSearch;
  onSelectSavedSearch: (savedSearch?: SavedSearch) => Promise<void>;
  onSearch: (searchParams: SearchParams) => void;
  searchParams: SearchParams;
  settingsComponents?: JSX.Element;
}): JSX.Element {
  const rootCrumbProps: {key: string; text: string; onClick?: () => void} = {
    key: 'root',
    text: 'Workflows',
  };
  if (props.activeSavedSearch != null) {
    rootCrumbProps.onClick = () => {
      props.onSearch(
        SearchUtil.clearSearchParams(props.searchParams, [], defaultProcessManagementSearchParams().filters)
      );
      props.onSelectSavedSearch(undefined);
    };
  }

  const getCrumbs = (): Array<JSX.Element> => {
    if (props.activeSavedSearch) {
      return [
        <CrumbLink {...rootCrumbProps} />,
        <CrumbLink key="root" text={props.activeSavedSearch.name} />,
      ];
    } else {
      return [<CrumbLink {...rootCrumbProps} />];
    }
  };

  return (
    <SplitBreadcrumbs
      crumbs={getCrumbs()}
      settingsIcon={
        <>
          <SettingsIconDropdown
            options={[
              [
                {
                  name: 'Workflow schemas',
                  handleOnClick: () => {
                    BenchlingUtil.navigate(null, ProcessSchemaDAO.processTaskSchemasURL());
                  },
                },
              ],
            ]}
          />
          {props.settingsComponents}
        </>
      }
    />
  );
}

export function getProcessManagementURL(
  prevProps: ProcessManagementSidepanelProps,
  props: ProcessManagementSidepanelProps
): NavigationUpdate | null {
  return props.isExpanded
    ? {
        url: getNavigationURL('/workflows', props.searchParams, props.filterErrors),
        options: {replace: prevProps.isExpanded === props.isExpanded},
      }
    : null;
}

function renderProcessManagementToolbar(
  items: Array<ListableItem> | null,
  selectedIds: Array<string>,
  searchParams: SearchParams
): JSX.Element {
  const selectedTypes = getSelectedTypes(searchParams);
  const selectedItems = (items?.filter((item) => selectedIds.includes(item.id)) ??
    []) as Array<SidepanelProcessItem>;

  const selectedClientOrigin = getSelectedClientOrigin(searchParams);

  return (
    <ProcessManagementToolbar
      selectedTypes={selectedTypes}
      selectedItems={selectedItems}
      selectedClientOrigin={selectedClientOrigin}
    />
  );
}

export function ProcessManagementSidepanel(props: ProcessManagementSidepanelProps): JSX.Element {
  const selectedTypes = getSelectedTypes(props.searchParams);
  const selectedClientOrigin = getSelectedClientOrigin(props.searchParams);

  const defaultHiddenColumnKeys = useMemo(() => {
    if (areSelectedTypesExactly(selectedTypes, [ModelTypeConstants.PROCESS])) {
      return [ColumnAccessorKey.PROCESS_NAME];
    } else if (areSelectedTypesExactly(selectedTypes, [ModelTypeConstants.PROCESS_TASK])) {
      return [ColumnAccessorKey.NAME];
    } else if (
      selectedTypes.length === 1 &&
      areSelectedTypesSubset(selectedTypes, getAvailableTypes(ProcessClientOrigin.RECIPES))
    ) {
      return [ColumnAccessorKey.PROCESS_TASK_TYPE]; // Hide type column if only one (recipes) type selected
    }
    return [];
  }, [selectedTypes]);

  const handleSetFilters = (filters: Array<Filter>): void => {
    if (selectedClientOrigin === ProcessClientOrigin.PROCESSES) {
      const newSelectedTypes = getSelectedTypes({
        ...props.searchParams,
        filters,
      });
      if (!areSelectedTypesExactly(newSelectedTypes, selectedTypes)) {
        ProcessManagementSidepanelPreferences.setTypeFilterPreference(newSelectedTypes);
      }
    }

    const newSelectedClientOrigin = getSelectedClientOrigin({
      ...props.searchParams,
      filters,
    });

    if (newSelectedClientOrigin !== selectedClientOrigin) {
      filters = getDefaultFiltersForClientOrigin(newSelectedClientOrigin);
    }

    props.onUpdateFilters(filters);
  };

  assert(
    areProcessDashboardModelTypesSupported(selectedClientOrigin, selectedTypes),
    'Expected the selected types to be a supported by the selected view',
    {
      selectedClientOrigin,
      selectedTypes,
      searchParams: props.searchParams,
    }
  );

  return (
    <div className="sidepanel">
      <ItemListing
        {...props}
        savedSearchSection={SearchSection.PROCESSES}
        onUpdateFilters={handleSetFilters}
        actionSource={ActionOrigin.WORKFLOWS_EXPANDED_SEARCH}
        availableTypes={getAvailableTypes(selectedClientOrigin)}
        availableQuickFilters={[QuickFilterKey.TASKS_ASSIGNED_TO_CURRENT_USER]}
        defaultTypes={getDefaultTypes(selectedClientOrigin)}
        isTypeRequired={getIsTypeRequired(selectedClientOrigin)}
        // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
        makeCrumbs={(settingsComponents?: JSX.Element) => (
          <ProcessManagementSidepanelCrumbs
            activeSavedSearch={props.activeSavedSearch}
            onSelectSavedSearch={props.onSelectSavedSearch}
            onSearch={props.onSearch}
            searchParams={props.searchParams}
            settingsComponents={settingsComponents}
          />
        )}
        additionalHeaderButtons={
          <NewProcessDropdown
            processTaskSchemas={props.schemasByType[ModelTypeConstants.PROCESS_TASK_SCHEMA] ?? null}
          />
        }
        primaryFilterOptionsBeforeTypeFilter={
          getEnabledClientOrigins().length > 1
            ? [
                {
                  filterKey: FilterKey.PROCESS_CLIENT_ORIGIN,
                },
              ]
            : []
        }
        extraPrimaryFilterOptions={getAvailablePrimaryFilters(selectedClientOrigin, selectedTypes)}
        getSecondaryFilterGroups={getAvailableSecondaryFilters(selectedClientOrigin)}
        availableSortKeys={PROCESS_SORT_KEYS}
        preservedFilterKeys={[FilterKey.TYPES, FilterKey.PROCESS_CLIENT_ORIGIN]}
        section={NavConstants.Section.PROCESSES}
        dataTestAttrs={{component: 'ProcessManagementSidepanel', element: 'root'}}
        renderToolbar={renderProcessManagementToolbar}
        defaultHiddenColumnKeys={defaultHiddenColumnKeys}
        showMultiKeySortMenu={shouldShowSortDropdown(selectedClientOrigin)}
        customSortables={getAvailableCustomSortables(selectedTypes)}
      />
    </div>
  );
}

export default withSearching(
  ProcessManagementSidepanel,
  SearchType.PROCESS_MANAGEMENT,
  NavConstants.Section.PROCESSES,
  {
    defaultSearchParams: defaultProcessManagementSearchParams(),
    getNavigationUpdate: getProcessManagementURL,
    savedSearchPreferenceKey: SearchPreferences.getSavedSearchPreferenceKey(NavConstants.Section.PROCESSES),
    filterKeysToRemember: [FilterKey.PROCESS_CLIENT_ORIGIN, FilterKey.TYPES],
  }
);
